import { useEffect, useState } from 'react';
import { chatModeFromFeatures } from '@/lib/chat-mode';
import { type EmbedContextValue, getEmbedContext, useEmbedContext } from '@/lib/embed-context';
import type { LauncherIcon, LauncherStyle } from '@/lib/embed-protocol';
import { getTransportConfig } from '@/lib/runtime-env';

/** Mirrors chat-handler's DistrictChatMode (derived from Features__c). */
export type DistrictChatMode = 'ai' | 'connect' | 'both';

export type EndpointStatus =
	| { state: 'loading' }
	| {
			state: 'enabled';
			features: string[];
			inviteRequired: boolean;
			/** Authoritative AI-vs-Connect routing signal. Absent on older backends. */
			chatMode?: DistrictChatMode;
			/** Greeting__c: the Amazon Connect contact-flow greeting, for the counselor ambient bubble only. */
			greeting?: string;
			/** District contact message (ChatInfoText__c) for the crisis card. */
			chatInfoText?: string;
			/** Retained for API compatibility; the student UI does not render this notice. */
			privacyNotice?: string;
			/** District accent color (ChatWidgetColor__c). */
			widgetColor?: string;
			/** ChatWidgetLauncherStyle__c — how a launcher is drawn. */
			launcherStyle?: LauncherStyle;
			/** ChatWidgetLabel__c, falling back to ChatWidgetHeading__c. */
			launcherLabel?: string;
			/** ChatWidgetIcon__c. */
			launcherIcon?: LauncherIcon;
			/** ChatWidgetHeight__c — initial height for an in-page mount. */
			launcherHeight?: number;
			/** ChatWidgetPosition__c. */
			launcherPosition?: 'bottom-right' | 'bottom-left';
	  }
	| { state: 'disabled' }
	// Treat fetch failures as enabled — backend joinSession still enforces the
	// kill switch, so we'd rather show a working chat than a false maintenance
	// page when the status route is briefly unreachable.
	| { state: 'unknown' };

/** sessionStorage key for the last-seen district contact info (crisis card
 * fallback when the probe is unreachable, e.g. fully offline PWA). */
export function chatInfoStorageKey(districtId: string): string {
	return `reachout:chat-info:${districtId}`;
}

/** Legacy default retained for compatibility. Not rendered in student UI. */
export const DEFAULT_PRIVACY_NOTICE =
	'This conversation is saved, and staff at your school may be able to read it. If you ask for a counselor, a real person joins the chat.';

const VALID_CHAT_MODES: ReadonlySet<string> = new Set(['ai', 'connect', 'both']);

function parseChatMode(value: unknown): DistrictChatMode | undefined {
	return typeof value === 'string' && VALID_CHAT_MODES.has(value)
		? (value as DistrictChatMode)
		: undefined;
}

function parseOptionalText(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

const LAUNCHER_STYLES: ReadonlySet<string> = new Set(['icon', 'pill', 'link', 'bare']);
const LAUNCHER_ICONS: ReadonlySet<string> = new Set([
	'chat',
	'heart',
	'lifebuoy',
	'sparkle',
	'shield',
	'none',
]);

/**
 * Presentation values are narrowed against a known set rather than passed
 * through. They originate in Salesforce free-text/picklist fields and end up
 * as CSS class suffixes and icon keys, so an unexpected value should fall back
 * to the default instead of reaching the DOM. Salesforce picklists make a bad
 * value unlikely; this makes it harmless.
 */
function parseLauncherStyle(value: unknown): LauncherStyle | undefined {
	const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
	return LAUNCHER_STYLES.has(key) ? (key as LauncherStyle) : undefined;
}

function parseLauncherIcon(value: unknown): LauncherIcon | undefined {
	const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
	return LAUNCHER_ICONS.has(key) ? (key as LauncherIcon) : undefined;
}

function parseHeight(value: unknown): number | undefined {
	const n = typeof value === 'number' ? value : Number.parseInt(String(value), 10);
	return Number.isFinite(n) ? Math.min(Math.max(n, 240), 1200) : undefined;
}

function parsePosition(value: unknown): 'bottom-right' | 'bottom-left' | undefined {
	const key = typeof value === 'string' ? value.trim().toLowerCase() : '';
	return key === 'bottom-left' || key === 'bottom-right' ? key : undefined;
}

/**
 * Build the status an in-page element already knows, instead of probing for it.
 *
 * An element on a school's origin cannot reach `endpoint-status`: the route is
 * not CORS-open to arbitrary origins and, today, has no HTTP surface in front of
 * it at all. So the generator bakes the district's config into the snippet the
 * school pastes and the element hands it over here — after which greeting,
 * privacy notice, crisis text, the invite gate and mode routing all resolve
 * through their existing code paths with no further special-casing.
 *
 * The trade is that a Salesforce change needs a re-paste for in-page districts.
 * That is called out in the install gallery and in the runbook.
 */
export function synthesizeEndpointStatus(
	ctx: EmbedContextValue,
	districtId: string | null,
): EndpointStatus {
	// Two elements for *different* districts on one page: the baked config
	// belongs to whichever asked, so anything else falls back to the fail-open
	// state rather than answering with the wrong district's greeting.
	if (ctx.district && districtId && ctx.district !== districtId) return { state: 'unknown' };

	const features = (ctx.features ?? '')
		.split(';')
		.map((feature) => feature.trim())
		.filter(Boolean);

	return {
		state: 'enabled',
		features,
		inviteRequired: ctx.inviteRequired ?? false,
		// Derived from the same Features__c string the backend derives it from,
		// so an in-page district routes identically to a framed one.
		chatMode: chatModeFromFeatures(ctx.features),
		greeting: parseOptionalText(ctx.greeting),
		chatInfoText: parseOptionalText(ctx.chatInfoText),
		privacyNotice: parseOptionalText(ctx.privacyNotice),
		widgetColor: parseOptionalText(ctx.accent),
		launcherHeight: parseHeight(ctx.launcherHeight),
	};
}

// Promise cache shares a single in-flight probe between the route loader and
// the hook; the resolved cache lets the hook initialize synchronously.
const statusPromises = new Map<string, Promise<EndpointStatus>>();
const resolvedStatuses = new Map<string, EndpointStatus>();

/**
 * Probe `endpoint-status` once per session per district. Shared between
 * `useEndpointStatus` and the `/chat` route loader (which races it against a
 * short timeout so routing never blocks on a slow Lambda).
 *
 * Belt-and-braces with the static maintenance page baked into the partner
 * HTML at generation time: if a school's CDN has the live page cached, the
 * runtime check still flips the UI to the maintenance state.
 */
export function probeEndpointStatus(districtId: string): Promise<EndpointStatus> {
	const existing = statusPromises.get(districtId);
	if (existing) return existing;

	// In-page: the config is already in hand and the route is unreachable from
	// here anyway. Not cached in the module maps — the element's own React
	// context is the authority, and caching would let one element's config leak
	// into another's route load.
	const ctx = getEmbedContext();
	if (ctx.source === 'element') {
		return Promise.resolve(synthesizeEndpointStatus(ctx, districtId));
	}

	const { chatHttpEndpoint } = getTransportConfig();
	if (!chatHttpEndpoint) {
		const settled: EndpointStatus = { state: 'unknown' };
		resolvedStatuses.set(districtId, settled);
		const promise = Promise.resolve(settled);
		statusPromises.set(districtId, promise);
		return promise;
	}

	const url = chatHttpEndpoint.replace(/\/$/, '');
	const promise = fetch(url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ action: 'endpoint-status', districtId }),
		credentials: 'omit',
	})
		.then((r) => (r.ok ? r.json() : Promise.reject(new Error(`status ${r.status}`))))
		.then(
			(body: {
				enabled?: boolean;
				features?: string[];
				inviteRequired?: boolean;
				chatMode?: string;
				greeting?: string;
				chatInfoText?: string;
				privacyNotice?: string;
				widgetColor?: string;
				launcherStyle?: string;
				launcherLabel?: string;
				launcherIcon?: string;
				launcherHeight?: number;
				launcherPosition?: string;
			}): EndpointStatus => {
				if (!body.enabled) return { state: 'disabled' };
				const chatInfoText = parseOptionalText(body.chatInfoText);
				if (chatInfoText) {
					try {
						sessionStorage.setItem(chatInfoStorageKey(districtId), chatInfoText);
					} catch {
						// Storage unavailable (private mode quota etc.) — crisis card
						// falls back to national defaults.
					}
				}
				return {
					state: 'enabled',
					features: body.features ?? [],
					inviteRequired: body.inviteRequired ?? false,
					chatMode: parseChatMode(body.chatMode),
					greeting: parseOptionalText(body.greeting),
					chatInfoText,
					privacyNotice: parseOptionalText(body.privacyNotice),
					widgetColor: parseOptionalText(body.widgetColor),
					launcherStyle: parseLauncherStyle(body.launcherStyle),
					launcherLabel: parseOptionalText(body.launcherLabel),
					launcherIcon: parseLauncherIcon(body.launcherIcon),
					launcherHeight: parseHeight(body.launcherHeight),
					launcherPosition: parsePosition(body.launcherPosition),
				};
			},
		)
		.catch((err): EndpointStatus => {
			console.warn('[endpoint-status] probe failed', err);
			return { state: 'unknown' };
		})
		.then((status) => {
			resolvedStatuses.set(districtId, status);
			return status;
		});

	statusPromises.set(districtId, promise);
	return promise;
}

/**
 * Race the shared probe against a timeout. Used by the `/chat` route loader
 * to make the endpoint-status result authoritative for mode routing without
 * ever stalling the student on a slow network — on timeout the probe keeps
 * running and `useEndpointStatus` picks the result up when it lands.
 */
export function probeEndpointStatusWithTimeout(
	districtId: string,
	timeoutMs: number,
): Promise<EndpointStatus> {
	return Promise.race([
		probeEndpointStatus(districtId),
		new Promise<EndpointStatus>((resolve) => {
			setTimeout(() => resolve({ state: 'loading' }), timeoutMs);
		}),
	]);
}

export function useEndpointStatus(districtId: string | null): EndpointStatus {
	const ctx = useEmbedContext();
	const inPage = ctx.source === 'element';

	const [status, setStatus] = useState<EndpointStatus>(() => {
		if (inPage) return synthesizeEndpointStatus(ctx, districtId);
		if (!districtId) return { state: 'unknown' };
		return resolvedStatuses.get(districtId) ?? { state: 'loading' };
	});

	useEffect(() => {
		// Read from this element's own context, never the module singleton, so a
		// second element on the page cannot retarget the first one's district.
		if (inPage) {
			setStatus(synthesizeEndpointStatus(ctx, districtId));
			return;
		}
		if (!districtId) {
			setStatus({ state: 'unknown' });
			return;
		}
		let cancelled = false;
		probeEndpointStatus(districtId).then((next) => {
			if (!cancelled) setStatus(next);
		});
		return () => {
			cancelled = true;
		};
	}, [districtId, inPage, ctx]);

	return status;
}
